import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { copyText } from '@/lib/clipboard';
import { toast } from 'sonner';
import { Copy, Pencil, Check, X, Plus, Loader2 } from 'lucide-react';

export default function EmailInlineCell({ employee, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const email = employee.email || '';

  const startEdit = () => { setValue(email); setEditing(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/employees/${employee.id}/email`, { email: value.trim() });
      toast.success(value.trim() ? 'Email saved' : 'Email cleared');
      setEditing(false);
      onSaved(employee.id, value.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save email');
    }
    setBusy(false);
  };

  const copy = async () => {
    const ok = await copyText(email);
    if (ok) toast.success('Email copied to clipboard');
    else toast.error('Could not copy');
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="name@company.com"
          className="h-7 w-48 text-xs"
          autoFocus
          data-testid={`email-input-${employee.id}`}
        />
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-emerald-600" onClick={save} disabled={busy} data-testid={`email-save-${employee.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => setEditing(false)}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  if (!email) {
    return (
      <button
        type="button"
        className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
        onClick={(e) => { e.stopPropagation(); startEdit(); }}
        data-testid={`email-add-${employee.id}`}
      >
        <Plus className="h-3 w-3" />Add email
      </button>
    );
  }

  return (
    <div className="flex items-center gap-0.5 group" onClick={(e) => e.stopPropagation()}>
      <span className="text-xs truncate max-w-[180px]" title={email} data-testid={`email-text-${employee.id}`}>{email}</span>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" onClick={copy} title="Copy email" data-testid={`email-copy-${employee.id}`}>
        <Copy className="h-3 w-3" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" onClick={startEdit} title="Edit email" data-testid={`email-edit-${employee.id}`}>
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
}
